import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, Divider, FormLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DialogActions from '@mui/material/DialogActions';
import { useTranslation } from 'react-i18next';
import PriorityDto from '../../../dtos/Priorities/PriorityDto';
import { HexColorPicker } from 'react-colorful';
import PriorityApiService from '../../../api/PriorityApiService';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const SOURCE_LOCALE = 'en';
const TRANSLATABLE_LOCALES = [
    { code: 'ru', label: 'Русский' },
    { code: 'fr', label: 'Français' },
];

export default function ModifyPriorityModal(props) {
    const { onClose, open, selectedPriority, priorityApiService, onCancel } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [priorityName, setPriorityName] = useState('');
    const [sortSequence, setSortSequence] = useState(0);
    const [backgroundColor, setBackgroundColor] = useState('');
    const [translations, setTranslations] = useState({});
    const [manualLocales, setManualLocales] = useState([]);

    const [nameError, setNameError] = useState('');
    const [sortSequenceError, setSortSequenceError] = useState('');

    const isEdit = Boolean(selectedPriority?.prId);

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        const priorityDto = new PriorityDto();
        priorityDto.prId = selectedPriority?.prId ?? 0;
        priorityDto.prName = priorityName;
        priorityDto.prSortSeq = sortSequence;
        priorityDto.prBackgroundColor = backgroundColor;
        priorityDto.names = { [SOURCE_LOCALE]: priorityName, ...translations };
        priorityDto.manualLocales = manualLocales;

        if(validateInfo() && selectedPriority && selectedPriority.prId) {
            const updatedPriority = await priorityApiService.updatePriority(priorityDto);

            if(updatedPriority.success) {
                snackbar.success(t('modifyPriority.toast.updated'));
                resetModal();
            } else {
                snackbar.error(t('modifyPriority.toast.updateError', { error: updatedPriority.message }));
            }
        } else if(validateInfo()) {
            const newPriority = await priorityApiService.createPriority(priorityDto);

            if(newPriority.success) {
                snackbar.success(t('modifyPriority.toast.added'));
                resetModal();
            } else {
                snackbar.error(t('modifyPriority.toast.addError', { error: newPriority.message }));
            }
        }
    });

    function validateInfo() {
        const errors = [];

        if(priorityName === null || priorityName === '') {
            errors.push(t('modifyPriority.validation.nameRequired'));
        }

        setNameError(!priorityName);

        if(sortSequence < 1) {
            errors.push(t('common.validation.sortSequence'));
        }

        setSortSequenceError(sortSequence < 1);

        if(backgroundColor === null || backgroundColor === '' || backgroundColor.length !== 7) {
            setBackgroundColor('#FFFFFF');
        }

        if(errors.length > 0) {
            snackbar.error(t('modifyPriority.validation.failed', { errors: errors.join(', ') }));
            return false;
        } else {
            return true;
        }
    }

    function resetModal() {
        setSortSequence(0);
        setPriorityName('');
        setBackgroundColor('');
        setTranslations({});
        setManualLocales([]);
        onClose();
    }

    function onTranslationChange(locale, value) {
        setTranslations(prev => ({ ...prev, [locale]: value }));
        if (!manualLocales.includes(locale)) {
            setManualLocales(prev => [...prev, locale]);
        }
    }

    function onClearOverride(locale) {
        setTranslations(prev => {
            const next = { ...prev };
            delete next[locale];
            return next;
        });
        setManualLocales(prev => prev.filter(l => l !== locale));
    }

    const helperText = useMemo(() => isEdit
        ? t('modifyPriority.translations.helperEdit')
        : t('modifyPriority.translations.helperCreate'), [isEdit, t]);

    useEffect(() => {
        if(open) {
            setPriorityName(selectedPriority?.prName ?? '');
            setSortSequence(selectedPriority?.prSortSeq ?? 0);
            setBackgroundColor(selectedPriority?.prBackgroundColor ?? '');
            const incomingNames = selectedPriority?.names ?? {};
            const nonSource = Object.fromEntries(
                Object.entries(incomingNames).filter(([locale]) => locale !== SOURCE_LOCALE)
            );
            setTranslations(nonSource);
            setManualLocales(selectedPriority?.manualLocales ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPriority, open])

    return(
        <Dialog
            open={open}
            sx={{ '& .MuiDialog-paper': { width: '100%' } }}
            maxWidth={'lg'}
            fullWidth
        >
            <DialogTitle>{selectedPriority?.prName ? t('modifyPriority.title.edit', { name: selectedPriority.prName }) : t('modifyPriority.title.create')}</DialogTitle>
            <DialogContent dividers>
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('modifyPriority.field.name')}</FormLabel>
                        <br/>
                        <TextField
                            error={nameError}
                            sx={{ width: '100%' }}
                            autoComplete={'Priority Name'}
                            value={priorityName}
                            onChange={(event) => setPriorityName(event.target.value)}
                        />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('fieldName.sortSequence')}</FormLabel>
                        <br/>
                        <TextField
                            error={sortSequenceError}
                            sx={{ width: '100%' }}
                            type={'number'}
                            autoComplete={'Sort Sequence'}
                            value={sortSequence}
                            onChange={(event) => setSortSequence(event.target.value)}
                        />
                    </Box>
                    <br/>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('modifyPriority.field.backgroundColor')}</FormLabel>
                        <HexColorPicker color={backgroundColor} onChange={setBackgroundColor} />
                        <TextField
                            sx={{ width: '100%' }}
                            value={backgroundColor}
                            onChange={(event) => setBackgroundColor(event.target.value)}
                        ></TextField>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                        {t('modifyPriority.translations.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {helperText}
                    </Typography>
                    <Stack spacing={1.5}>
                        {TRANSLATABLE_LOCALES.map(({ code, label }) => {
                            const isManual = manualLocales.includes(code);
                            return (
                                <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormLabel sx={{ minWidth: 90 }}>{label}</FormLabel>
                                    <TextField
                                        size="small"
                                        sx={{ flex: 1 }}
                                        value={translations[code] ?? ''}
                                        placeholder={isEdit ? '' : t('modifyPriority.translations.willGenerate')}
                                        onChange={(e) => onTranslationChange(code, e.target.value)}
                                        InputProps={{
                                            endAdornment: isManual ? (
                                                <Tooltip title={t('modifyPriority.translations.regenerateTooltip')}>
                                                    <IconButton size="small" onClick={() => onClearOverride(code)}>
                                                        <RefreshRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null,
                                        }}
                                        helperText={isManual ? t('modifyPriority.translations.manualBadge') : t('modifyPriority.translations.autoBadge')}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus variant={'contained'} onClick={onCancel} disabled={saving}>{t('common.cancel')}</Button>
                <SaveButton loading={saving} variant={'contained'} onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}

ModifyPriorityModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    selectedPriority: PropTypes.objectOf(PriorityDto),
    priorityApiService: PropTypes.objectOf(PriorityApiService).isRequired,
    onCancel: PropTypes.func.isRequired,
}
